import api from "../network/api";
import { ApiError, ApiResponse } from "../network/axios";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";

// ----------------- Interfaces -----------------

interface CreateCheckoutSessionPayload {
  fullName: string;
  email: string;
}

interface Ticket {
  _id: string;
  fullName: string;
  email: string;
  status: "attending" | "cancelled";
  createdAt: string;
}

interface TicketInfo {
  fullName: string;
  email: string;
  status: "attending" | "cancelled";
}

interface CancelRsvpResponse {
  fullName: string;
  email: string;
}

interface ReminderResult {
  total: number;
  sent: number;
  failed: number;
  errors: Array<{ email: string; error: string }>;
}

interface SingleReminderResponse {
  fullName: string;
  email: string;
}

// ----------------- API Calls -----------------

// Create RSVP
export const orderTicket = async (
  payload: CreateCheckoutSessionPayload
): Promise<ApiResponse> => {
  const res = await api.post<ApiResponse>("/rsvp/create", payload);
  return res;
};

export const useOrderTicket = () => {
  return useMutation<ApiResponse, ApiError, CreateCheckoutSessionPayload>({
    mutationFn: orderTicket,
    onSuccess: () => {
      toast("Yayy!!!", {
        description: "Your RSVP has been received.",
      });
    },
    onError: (error: ApiError) => {
      toast("Operation Failed", {
        description: error.response?.data?.message || "Something went wrong!",
      });
    },
  });
};

// Get all tickets
export const getAllTickets = async (): Promise<ApiResponse<Ticket[]>> => {
  const res = await api.get<ApiResponse<Ticket[]>>(`/rsvp`);
  return res;
};

export const useGetAllTickets = () => {
  return useQuery<ApiResponse<Ticket[]>, ApiError>({
    queryKey: ["tickets"],
    queryFn: () => getAllTickets(),
    retry: 2,
    staleTime: 5 * 60 * 1000,
  });
};

// Get ticket info by token
export const getTicketByToken = async (
  token: string
): Promise<ApiResponse<TicketInfo>> => {
  const res = await api.get<ApiResponse<TicketInfo>>(`/rsvp/unrsvp/${token}`);
  return res;
};

export const useGetTicketByToken = (token: string) => {
  return useQuery<ApiResponse<TicketInfo>, ApiError>({
    queryKey: ["ticket", token],
    queryFn: () => getTicketByToken(token),
    enabled: !!token,
    retry: 1,
  });
};

// Cancel RSVP
export const cancelRsvp = async (
  token: string
): Promise<ApiResponse<CancelRsvpResponse>> => {
  const res = await api.post<ApiResponse<CancelRsvpResponse>>(
    `/rsvp/unrsvp/${token}`,
    {}
  );
  return res;
};

export const useCancelRsvp = () => {
  return useMutation<ApiResponse<CancelRsvpResponse>, ApiError, string>({
    mutationFn: cancelRsvp,
    onSuccess: () => {
      toast("RSVP Cancelled", {
        description: "Your RSVP has been successfully cancelled.",
      });
    },
    onError: (error: ApiError) => {
      toast("Cancellation Failed", {
        description: error.response?.data?.message || "Something went wrong!",
      });
    },
  });
};

// Send reminder emails to all attending guests
export const sendBulkReminder = async (): Promise<
  ApiResponse<ReminderResult>
> => {
  const res = await api.post<ApiResponse<ReminderResult>>(
    `/rsvp/reminder/send-all`,
    {}
  );
  return res;
};

export const useSendBulkReminder = () => {
  return useMutation<ApiResponse<ReminderResult>, ApiError, void>({
    mutationFn: sendBulkReminder,
    onSuccess: (data) => {
      const result = data.data;
      if (result.failed === 0) {
        toast("Reminders Sent!", {
          description: `Successfully sent ${result.sent} reminder emails.`,
        });
      } else {
        toast("Reminders Partially Sent", {
          description: `Sent ${result.sent} of ${result.total}. ${result.failed} failed.`,
        });
      }
    },
    onError: (error: ApiError) => {
      toast("Failed to Send Reminders", {
        description: error.response?.data?.message || "Something went wrong!",
      });
    },
  });
};

// Send reminder email to a single guest
export const sendSingleReminder = async (
  ticketId: string
): Promise<ApiResponse<SingleReminderResponse>> => {
  const res = await api.post<ApiResponse<SingleReminderResponse>>(
    `/rsvp/reminder/send/${ticketId}`,
    {}
  );
  return res;
};

export const useSendSingleReminder = () => {
  return useMutation<ApiResponse<SingleReminderResponse>, ApiError, string>({
    mutationFn: sendSingleReminder,
    onSuccess: (data) => {
      toast("Reminder Sent!", {
        description: `Reminder email sent to ${data.data.fullName}.`,
      });
    },
    onError: (error: ApiError) => {
      toast("Failed to Send Reminder", {
        description: error.response?.data?.message || "Something went wrong!",
      });
    },
  });
};

// Delete ticket API call
const deleteTicket = async (ticketId: string): Promise<ApiResponse<null>> => {
  const response = await api.delete<ApiResponse<null>>(`/rsvp/${ticketId}`);
  return response;
};

// Delete ticket hook
export const useDeleteTicket = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: deleteTicket,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["del_tickets"] });
      toast("Guest Deleted", {
        description: "The guest has been removed from the list.",
      });
    },
    onError: (error: ApiError) => {
      toast("Delete Failed", {
        description: error.response?.data?.message || "Something went wrong!",
      });
    },
  });
};
